/**
 * Filter sidebar for PLP.
 *
 * Displays facet filters in an accordion-style layout.
 * Uses nuqs for URL state management.
 *
 * Filter types:
 * - SortFilter: Sort options (first for Breitling)
 * - CollectionFilter: Image grid for collections (brand-specific images)
 * - SwatchFilter: Color swatches for case materials and dial colors
 * - FacetFilter: Standard checkbox list for other facets
 * - PriceSliderFilter: Dual-handle price range slider
 * - RefNumberFilter: Reference/SKU search (in accordion for Breitling)
 */

'use client';

import { Icon } from '@/components/ui/Icon';
import type { FacetResults, FilterState, SortOption } from '@/lib/algolia';
import { isCollectionFacet, isSwatchFacet } from '@/lib/algolia/colorMappings';
import { getBaseFacetName } from '@/lib/algolia/facets';
import { BRAND } from '@/lib/cms/config';
import { devLog } from '@/lib/utils/devLog';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { CollectionFilter } from '../CollectionFilter';
import { FacetFilter } from '../FacetFilter';
import { PriceSliderFilter } from '../PriceSliderFilter';
import { RefNumberFilter } from '../RefNumberFilter';
import { SortFilter } from '../SortFilter';
import { SwatchFilter } from '../SwatchFilter';
import styles from './FilterBar.module.scss';

interface FilterBarProps {
  facets: FacetResults;
  activeFilters: FilterState;
  /** Hide the header when used inside a modal that already has a title */
  hideHeader?: boolean;
  /** Remove border/radius when embedded in a modal */
  embedded?: boolean;
  /** Optional: Collection data from CMS with images */
  collectionData?: Array<{
    name: string;
    image?: string;
  }>;
  /** Optional: All available facets (for showing full options even when filtered) */
  allFacets?: FacetResults;
  /** Current sort option */
  currentSort?: SortOption;
  /** Product label - used to determine which filters to show (e.g., "stores" hides sort/ref) */
  productLabel?: string;
}

/**
 * Map Algolia facet base names to translation keys.
 * These keys correspond to plp.facets.* in message files.
 */
const FACET_TRANSLATION_KEYS: Record<string, string> = {
  // Watch facets
  collection: 'collection',
  size: 'size',
  case: 'case',
  dial_color: 'dialColor',
  type: 'type',
  price: 'price',
  bracelet_category_material: 'braceletMaterial',
  bracelet_category_color: 'braceletColor',
  // Strap facets
  material: 'material',
  color: 'color',
  lug_size: 'lugWidth',
  lug_type: 'lugType',
  buckle_type: 'buckle',
  // Eyewear facets
  gender: 'gender',
  frame_shape: 'frameShape',
  frame_material: 'frameMaterial',
  frame_color: 'frameColor',
  lens_color: 'lensColor',
  // Store facets
  store_type: 'storeType',
};

export function FilterBar({
  facets,
  activeFilters,
  hideHeader = false,
  embedded = false,
  collectionData,
  allFacets,
  currentSort = 'relevance',
  productLabel,
}: FilterBarProps) {
  const t = useTranslations('plp');

  // Debug logging
  devLog('[FilterBar] activeFilters:', activeFilters);
  devLog('[FilterBar] facets keys:', Object.keys(facets));
  devLog('[FilterBar] allFacets available:', !!allFacets);
  devLog('[FilterBar] allFacets keys:', allFacets ? Object.keys(allFacets) : 'none');

  // Use allFacets for display (shows all options) but facets for counts
  const displayFacets = allFacets || facets;
  devLog('[FilterBar] displayFacets keys:', Object.keys(displayFacets));
  const countFacets = facets;

  // Map facets to display format
  const facetEntries = Object.entries(displayFacets).map(([facetName, values]) => {
    const baseName = getBaseFacetName(facetName);
    const translationKey = FACET_TRANSLATION_KEYS[baseName];
    // Use translation if available, otherwise fall back to base name
    const displayName = translationKey ? t(`facets.${translationKey}`) : baseName;

    // Map facet name to URL param name in FilterState
    // Note: store_type maps to a custom key, not in FilterState
    const paramMap: Record<string, string> = {
      // Watch facets
      collection: 'collection',
      size: 'size',
      case: 'case',
      dial_color: 'dialColor',
      type: 'type',
      bracelet_category_material: 'braceletMaterial',
      bracelet_category_color: 'braceletColor',
      // Strap facets
      material: 'material',
      color: 'color',
      lug_size: 'lug',
      lug_type: 'lugType',
      buckle_type: 'buckle',
      // Eyewear facets
      gender: 'gender',
      frame_shape: 'frameShape',
      frame_material: 'frameMaterial',
      frame_color: 'frameColor',
      lens_color: 'lensColor',
      // Store facets
      store_type: 'storeType',
    };

    const paramName = paramMap[baseName];
    // Get the IDs from activeFilters (these are already IDs from URL params)
    // Cast to Record to allow indexing with store_type which may not be in FilterState
    const activeIds = paramName
      ? (activeFilters as Record<string, string[] | undefined>)[paramName]
      : undefined;

    // Get counts from the filtered facets (not allFacets)
    const countsFromFiltered = countFacets[facetName] || {};

    // Debug logging for each facet
    devLog(
      `[FilterBar] Facet "${facetName}" -> baseName="${baseName}", paramName="${paramName}", activeIds=`,
      activeIds
    );

    // Merge values: use all options from displayFacets, but counts from countFacets
    // Filter out zero-count values - they represent options from allFacets that don't exist
    // in the current page context (e.g., case materials not available in this collection).
    // With disjunctive faceting, if a value has 0 count, it means NO products match that
    // value in the current search context, so there's no point displaying it.
    const mergedValues: [string, number][] = Object.entries(values)
      .map(([value, _count]): [string, number] => {
        // Get the count from filtered results, or 0 if not present
        const filteredCount = countsFromFiltered[value] ?? 0;
        return [value, filteredCount];
      })
      .filter(([, count]) => count > 0) // Remove zero-count values
      .sort((a, b) => (b[1] as number) - (a[1] as number)); // Sort by count desc

    return {
      facetName,
      baseName,
      displayName,
      values: mergedValues,
      activeIds: activeIds || [],
    };
  });

  // Filter out price facet (handled separately) and empty facets
  const filteredFacets = facetEntries.filter((f) => f.baseName !== 'price' && f.values.length > 0);

  // Get price range from facets
  const priceFacet = facets['price'];
  const hasPriceFilter = priceFacet && Object.keys(priceFacet).length > 0;

  // Derive the slider's absolute bounds from the UNFILTERED facets (allFacets),
  // never from the active price-filtered facet. The price selection is applied as
  // a base filter, so the active facet only contains prices within the current
  // range — using it would collapse the bounds to the selection and make it
  // impossible to widen again. allFacets is independent of the price selection.
  const priceBoundsFacet = allFacets?.['price'] ?? priceFacet;
  const priceBounds = priceBoundsFacet
    ? Object.keys(priceBoundsFacet)
        .map(Number)
        .filter((n) => !Number.isNaN(n))
    : [];
  const priceMinBound = priceBounds.length ? Math.floor(Math.min(...priceBounds)) : undefined;
  const priceMaxBound = priceBounds.length ? Math.ceil(Math.max(...priceBounds)) : undefined;

  return (
    <div className={`${styles.filterBar} ${embedded ? styles.embedded : ''}`}>
      {!hideHeader && (
        <div className={styles.header}>
          <h2 className={styles.title}>{t('filters')}</h2>
        </div>
      )}

      <div className={styles.filters}>
        {/* Sort By - shown first in the drawer (hide for stores) */}
        {productLabel !== 'stores' && <SortFilter currentSort={currentSort} />}

        {/* Reference/SKU search — Breitling only (hidden for Gallet per BEC-23965) */}
        {productLabel !== 'stores' && BRAND === 'breitling' && (
          <RefNumberAccordion
            placeholder={t('facets.referencePlaceholder')}
            label={t('facets.reference')}
          />
        )}

        {filteredFacets.map(({ facetName, baseName, displayName, values, activeIds }) => {
          // Use CollectionFilter for collection facet
          if (isCollectionFacet(baseName)) {
            return (
              <CollectionFilter
                key={facetName}
                displayName={displayName}
                values={values}
                activeIds={activeIds}
                collectionData={collectionData}
              />
            );
          }

          // Use SwatchFilter for case and dial_color facets
          if (isSwatchFacet(baseName)) {
            return (
              <SwatchFilter
                key={facetName}
                name={baseName}
                displayName={displayName}
                values={values}
                activeIds={activeIds}
              />
            );
          }

          // Use standard FacetFilter for other facets
          return (
            <FacetFilter
              key={facetName}
              name={baseName}
              displayName={displayName}
              values={values}
              activeIds={activeIds}
            />
          );
        })}

        {/* Price slider filter (hide for stores) */}
        {hasPriceFilter && productLabel !== 'stores' && (
          <PriceSliderFilter
            minPrice={activeFilters.priceMin}
            maxPrice={activeFilters.priceMax}
            absoluteMin={priceMinBound}
            absoluteMax={priceMaxBound}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Reference number filter wrapped in accordion for Breitling
 */
function RefNumberAccordion({ placeholder, label }: { placeholder: string; label: string }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className={`${styles.refAccordion} ${isExpanded ? styles.expanded : ''}`}>
      <button
        type="button"
        className={styles.refAccordionHeader}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className={styles.refAccordionName}>{label}</span>
        <Icon
          name="chevron-down"
          size={20}
          className={`${styles.chevron} ${isExpanded ? styles.expanded : ''}`}
        />
      </button>
      {isExpanded && (
        <div className={styles.refAccordionContent}>
          <RefNumberFilter placeholder={placeholder} />
        </div>
      )}
    </div>
  );
}
